import { apiClient } from '../../../shared/lib/http';
import { buildFullName } from '../../../shared/lib/personName';
import { hasExplicitTime, isMidnightPlaceholder, normalizeMovementType } from '../utils/voucherMovements';
import { pickBestDateTime } from '../utils/voucherDates';

function normalizeMovement(movement, voucher = null) {
  const type = normalizeMovementType(movement.type);
  const fallbackPerformedAt = pickBestDateTime(voucher?.purchaseDate, voucher?.createdAt);
  const movementPerformedAt = pickBestDateTime(movement.performedAt || movement.date, movement.createdAt);
  const performedAt = type === 'issue' && (!hasExplicitTime(movementPerformedAt) || isMidnightPlaceholder(movementPerformedAt))
    ? (fallbackPerformedAt || movementPerformedAt)
    : movementPerformedAt;

  return {
    id: movement.id || movement._id,
    type,
    hours: Number(movement.hours || movement.durationHours || movement.amount || 0),
    performedAt,
    performedByName: movement.performedByName || movement.employeeName || movement.userName || '',
    note: movement.note || '',
  };
}

function normalizeVoucher(voucher) {
  const movements = Array.isArray(voucher.movements)
    ? voucher.movements.map((movement) => normalizeMovement(movement, voucher))
    : [];

  const issuedHours = Number(voucher.hoursPurchased || voucher.totalHours || voucher.issuedHours || 0);
  const redeemedHours = movements
    .filter((movement) => movement.type === 'redeem')
    .reduce((total, movement) => total + movement.hours, 0);
  const adjustedHours = movements
    .filter((movement) => movement.type === 'adjust')
    .reduce((total, movement) => total + movement.hours, 0);
  const explicitHoursAvailable = Number(voucher.hoursAvailable);
  const computedHoursAvailable = issuedHours + adjustedHours - redeemedHours;
  const hasMovementData = movements.length > 0;

  return {
    id: voucher.id || voucher._id,
    voucherCode: voucher.voucherCode || voucher.code || voucher.id,
    customerId: voucher.customerId || voucher.customer?.id || voucher.customer?._id,
    customerName: voucher.customerName || voucher.customer?.fullName || buildFullName(voucher.customer || voucher),
    name: voucher.name || voucher.customer?.name || '',
    surname: voucher.surname || voucher.customer?.surname || '',
    phone: voucher.phone || voucher.customer?.phone || '',
    email: voucher.email || voucher.customer?.email || '',
    createdAt: voucher.createdAt || null,
    purchaseDate: pickBestDateTime(voucher.purchaseDate, voucher.createdAt),
    expiresAt: voucher.expiresAt || null,
    hoursPurchased: issuedHours,
    hoursUsed: Number(voucher.hoursUsed ?? redeemedHours ?? 0),
    hoursAvailable: hasMovementData
      ? computedHoursAvailable
      : (Number.isFinite(explicitHoursAvailable) ? explicitHoursAvailable : issuedHours - Number(voucher.hoursUsed ?? redeemedHours ?? 0)),
    status: voucher.status || 'active',
    operationType: voucher.operationType || null,
    movements,
  };
}

function normalizeVoucherSummary(voucher) {
  return {
    id: voucher.id || voucher._id,
    voucherCode: voucher.voucherCode || voucher.code || voucher.id,
    customerName: voucher.customerName || '',
    phone: voucher.phone || '',
    createdAt: voucher.createdAt || null,
    purchaseDate: pickBestDateTime(voucher.purchaseDate, voucher.createdAt),
    expiresAt: voucher.expiresAt || null,
    hoursPurchased: Number(voucher.hoursPurchased || voucher.totalHours || voucher.issuedHours || 0),
    hoursUsed: Number(voucher.hoursUsed || 0),
    hoursAvailable: Number(voucher.hoursAvailable || 0),
    status: voucher.status || 'active',
    lastRedeemAt: pickBestDateTime(voucher.lastRedeemAt || voucher.lastUseAt, null),
  };
}

function normalizeCustomerResult(entry) {
  const vouchers = Array.isArray(entry.vouchers) ? entry.vouchers.map(normalizeVoucherSummary) : [];
  const hoursAvailable = vouchers.reduce((total, voucher) => total + voucher.hoursAvailable, 0);

  return {
    id: entry.id || entry._id,
    fullName: entry.fullName || buildFullName(entry),
    name: entry.name || '',
    surname: entry.surname || '',
    email: entry.email || '',
    phone: entry.phone || '',
    profileType: entry.profileType || null,
    vouchers,
    hoursAvailable,
  };
}

function normalizePageResponse(data, itemNormalizer) {
  const items = Array.isArray(data?.items) ? data.items.map(itemNormalizer) : [];
  const size = Number(data?.size ?? items.length ?? 0) || items.length || 1;
  const totalElements = Number(data?.totalElements ?? items.length) || items.length;
  const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(totalElements / size))) || 1;

  return {
    items,
    page: data?.page ?? 0,
    size,
    totalElements,
    totalPages,
    sortBy: data?.sortBy ?? null,
    direction: data?.direction ?? null,
  };
}

export async function lookupVoucherForCustomer({ voucherCode, phone }) {
  const { data } = await apiClient.get('/vouchers/public/lookup', {
    params: {
      voucherCode,
      phone,
    },
  });

  return normalizeVoucher(data);
}

export async function searchCustomersByPhone(phone, params = {}) {
  const { data } = await apiClient.get('/vouchers/search-by-phone', {
    params: { phone, ...params },
  });

  return normalizePageResponse(data, normalizeCustomerResult);
}

export async function issueVoucher(payload) {
  const { data } = await apiClient.post('/vouchers', payload);
  return normalizeVoucher(data);
}

export async function getVoucher(voucherId) {
  const { data } = await apiClient.get(`/vouchers/${voucherId}`);
  return normalizeVoucher(data);
}

export async function deleteVoucher(voucherId) {
  await apiClient.delete(`/vouchers/${voucherId}`);
}

export async function redeemVoucher(voucherId, payload) {
  const { data } = await apiClient.post(`/vouchers/${voucherId}/redeem`, payload);
  return normalizeVoucher(data);
}

export async function adjustVoucher(voucherId, payload) {
  const { data } = await apiClient.post(`/vouchers/${voucherId}/adjust`, payload);
  return normalizeVoucher(data);
}

export async function getVoucherMovements(voucherId, params = {}) {
  const { data } = await apiClient.get(`/vouchers/${voucherId}/movements`, {
    params,
  });
  return normalizePageResponse(data, normalizeMovement);
}

export async function listAllVouchers(params = {}) {
  const { data } = await apiClient.get('/vouchers', {
    params,
  });
  return normalizePageResponse(data, normalizeVoucher);
}
